/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';

import StrikeIcon from '../../../../../../assets/icons/StrikeIcon';
import TimezoneBadge from '../../../../../../assets/icons/TimezoneBadge';
import TextLink from '../../../../../../components/TextLink/TextLink';
import { BLACK, FEED_BACK, PRIMARY_PURPLE, RED } from '../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../theme/fontConstants';
import {
  epochToDateInReadableFormat,
  epochToDayOfWeek,
  epochToTimeInReadableFormat,
  epochToTimezoneAbbr,
} from '../../../../../../utils';
import { ROUTE_CONSTANTS } from '../../../../../app/RouteBuilding/routeConstants';
import {
  INFRACTION_DISPLAY,
  INFRACTION_SUBTEXT,
  INFRACTION_TYPE,
} from '../../../../UsersConstants';

const generateInfraction = (type) => INFRACTION_DISPLAY[type];
const generateInfractionSubtext = (type) => INFRACTION_SUBTEXT[type];
const InfractionCard = ({ infraction, suspensionName, handleOnExcuseOrReinstate }) => (
  <Card
    sx={{
      mb: 2,
      borderRadius: '16px',
      border: `1px solid ${BLACK[40]}`,
      elevation: 0,
      boxShadow: 'none',
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: PRIMARY_FONT[300],
              color: BLACK[70],
              pt: 1,
              textTransform: 'uppercase',
            }}
          >
            {LanguageConverterV2('userDetail.attendance.relatedShift')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 0.5 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontFamily: PRIMARY_FONT[500],
              }}
            >
              {infraction?.start &&
                infraction?.end &&
                LanguageConverterV2('userDetail.attendance.placementTime', {
                  dayOfWeek: epochToDayOfWeek(infraction?.start),
                  date: epochToDateInReadableFormat(infraction?.start),
                  startTime: epochToTimeInReadableFormat(infraction?.start),
                  endTime: epochToTimeInReadableFormat(infraction?.end),
                })}
            </Typography>
            {infraction?.start && (
              <TimezoneBadge timezone={epochToTimezoneAbbr(infraction.start)} />
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Typography
              sx={{
                fontSize: '16px',
              }}
            >
              {`${infraction?.corpName} -`}
            </Typography>
            <TextLink
              to={
                infraction?.jobId ? `/${ROUTE_CONSTANTS.JOB_ORDERS}/${infraction.jobId}` : undefined
              }
              sx={{
                fontFamily: PRIMARY_FONT[400],
                color: PRIMARY_PURPLE[70],
                fontSize: '16px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {infraction?.jobName}
            </TextLink>
          </Box>
          {infraction?.cancelReason && infraction?.infractionType !== INFRACTION_TYPE.NCNS && (
            <>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontFamily: PRIMARY_FONT[300],
                  color: BLACK[70],
                  pt: 1,
                  textTransform: 'uppercase',
                }}
              >
                {LanguageConverterV2('userDetail.attendance.cancellationReason')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: PRIMARY_FONT[400],
                  color: BLACK[100],
                }}
              >
                {infraction?.cancelReason}
              </Typography>
            </>
          )}
        </Box>
        <Box
          sx={{
            width: '180px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {infraction?.excused ? (
              <Typography
                sx={{
                  fontSize: '18px',
                  fontFamily: PRIMARY_FONT[700],
                  color: BLACK[60],
                }}
              >
                {LanguageConverterV2(generateInfraction(infraction?.infractionType))}
              </Typography>
            ) : (
              <>
                <StrikeIcon sx={{ fontSize: 20 }} />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontFamily: PRIMARY_FONT[700],
                    color: FEED_BACK.RED_75,
                  }}
                >
                  {LanguageConverterV2(generateInfraction(infraction?.infractionType))}
                </Typography>
              </>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: PRIMARY_FONT[400],
              color: BLACK[60],
              textAlign: 'right',
            }}
          >
            {LanguageConverterV2(generateInfractionSubtext(infraction?.infractionType))}
          </Typography>

          {infraction?.cancelEpoch && infraction?.infractionType !== INFRACTION_TYPE.NCNS && (
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: PRIMARY_FONT[400],
                color: BLACK[60],
                textAlign: 'right',
              }}
            >
              {LanguageConverterV2('userDetail.attendance.cancelledOn', {
                timezone: epochToTimezoneAbbr(infraction?.cancelEpoch),
                time: epochToTimeInReadableFormat(infraction?.cancelEpoch),
                date: epochToDateInReadableFormat(infraction?.cancelEpoch),
              })}
            </Typography>
          )}
          {suspensionName && (
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[500],
                color: RED[80],
                fontSize: '12px',
                textAlign: 'right',
              }}
            >
              {LanguageConverterV2('userDetail.attendance.appliedTowards', {
                suspensionName: suspensionName || '',
              })}
              {infraction?.expirationOfInfraction &&
                LanguageConverterV2('userDetail.attendance.expirationOfInfraction', {
                  date: epochToDateInReadableFormat(infraction?.expirationOfInfraction),
                })}
            </Typography>
          )}
          <Button
            variant={infraction?.excused ? 'outlined' : 'contained'}
            sx={{
              mt: 3,
              width: '100px',
              fontFamily: PRIMARY_FONT[500],
              fontSize: '16px',
              textTransform: 'none',
            }}
            onClick={() => handleOnExcuseOrReinstate(infraction)}
          >
            {infraction?.excused
              ? LanguageConverterV2('userDetail.attendance.reinstate')
              : LanguageConverterV2('userDetail.attendance.excuse')}
          </Button>
        </Box>
      </Box>
    </CardContent>
    {infraction?.excused && (
      <Box
        sx={{
          backgroundColor: PRIMARY_PURPLE[20],
          py: 0.5,
          px: 1,
          textAlign: 'center',
          borderBottomLeftRadius: '16px',
          borderBottomRightRadius: '16px',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: PRIMARY_FONT[400],
            color: PRIMARY_PURPLE[70],
          }}
        >
          {LanguageConverterV2('userDetail.attendance.excusedBy', {
            user: infraction?.excused?.excuserName,
            time: epochToTimeInReadableFormat(infraction?.excused?.time),
            date: epochToDateInReadableFormat(infraction?.excused?.time),
          })}
        </Typography>
      </Box>
    )}
  </Card>
);

InfractionCard.propTypes = {
  infraction: PropTypes.shape({
    date: PropTypes.number,
    type: PropTypes.string,
    corporation: PropTypes.string,
    title: PropTypes.string,
    cancelNote: PropTypes.string,
  }),
  suspensionName: PropTypes.string,
  handleOnExcuseOrReinstate: PropTypes.func,
};

export default InfractionCard;
